<template>
  <Modal
    content-class="full-height column no-wrap overflow-hidden"
    :full-height="isMobile"
    header-class=""
    :modal-style="modalStyle"
    persistent
    @close="closeModal"
  >
    <template #header>
      <div
        class="tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-3"
        data-testid="header"
      >
        <div class="tw:text-lg tw:font-bold">
          {{ modalTitle }}
        </div>

        <Tag
          v-if="totalFilters"
          color="secondary"
          data-testid="total-filters"
          hide-cursor
          hide-remove-button
          :label="totalFilters"
          rounded
        />
      </div>
    </template>

    <div class="tw:flex tw:h-full tw:flex-1">
      <template v-if="!isMobile">
        <div
          class="tw:flex tw:w-[300px] tw:shrink-0 tw:flex-col tw:overflow-auto tw:py-4 tw:pl-4"
        >
          <SourceFilters
            class="tw:w-[268px]"
            data-testid="source-filters"
            :editing-stream="editingStream"
            :enabled-media="enabledMedia"
            :read-only="readOnly"
            :stream="stream"
            @edit-stream="editStream"
          />
        </div>
        <div class="tw:w-px tw:bg-denim-200" />
      </template>
      <SourceListSelection
        v-model:selected-sources="selectedSources"
        v-model:view="view"
        data-testid="source-list-selection"
        :editing-stream="editingStream"
        :read-only="readOnly"
        :stream="stream"
        @edit-stream="editStream"
      />
    </div>

    <template #buttons>
      <div class="tw:flex tw:flex-1 tw:justify-between">
        <div>
          <BaseButton
            v-if="view === 'selected' && selectedSources.length && !readOnly"
            data-testid="clear-selection"
            flat
            :label="
              getLocaleText(
                'modals.advanced_source_filters_modal.clear_selection_label'
              )
            "
            new-design
            @click="clear"
          />
          <BaseButton
            v-if="view === 'results' && (totalFilters || sourcesListQuery)"
            data-testid="clear-results"
            flat
            :label="
              getLocaleText(
                'modals.advanced_source_filters_modal.clear_filters_label'
              )
            "
            new-design
            @click="clear"
          />
        </div>

        <div>
          <BaseButton
            data-testid="cancel-button"
            :label="getLocaleText('global.cancel')"
            new-design
            @click="closeModal"
          />
          <BaseButton
            color="primary"
            data-testid="confirm-button"
            :disabled="readOnly"
            :label="getLocaleText('global.confirm')"
            new-design
            @click="confirm"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { type CSSProperties, computed, inject, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, Tag } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import SourceFilters from "shared/components/sources/SourceFilters.vue";
import SourceListSelection, {
  type SourceListSelectionView,
} from "shared/components/sources/SourceListSelection.vue";
import useSourceFilters from "shared/composables/useSourceFilters";
import type { MediumName } from "shared/helpers/media";
import { safePush } from "shared/helpers/routing";
import type { Source } from "shared/helpers/sourceFilters";
import type { Nullable, Stream } from "shared/types";

const { totalFilters, clearFilters } = useSourceFilters();

const router = useRouter();
const safeRouterPush = safePush({ router });

const selectedSources = defineModel<Source[]>("selectedSources", {
  default: () => [],
});

export interface AdvancedSourceFiltersModalProps {
  stream?: Nullable<Stream>;
  initialView?: SourceListSelectionView;
  readOnly?: boolean;
  editingStream?: boolean;
  enabledMedia?: MediumName[];
}

const props = withDefaults(defineProps<AdvancedSourceFiltersModalProps>(), {
  stream: null,
  initialView: "results",
  enabledMedia: () => [],
});

const emit = defineEmits<{
  close: [];
  selectedSources: [Source[]];
}>();

const isMobile = inject<boolean>("isMobile");

const view = ref<SourceListSelectionView>(
  isMobile ? "selected" : props.initialView
);

const sourcesListQuery = ref("");
const selectedSourcesQuery = ref("");

const modalTitle = computed<string>(() => {
  if (isMobile) {
    return getLocaleText("modals.advanced_source_filters_modal.mobile_title");
  }

  return getLocaleText("modals.advanced_source_filters_modal.title");
});

const modalStyle = computed<CSSProperties>(() => {
  if (isMobile) {
    return {};
  }

  return { maxWidth: "860px", height: "640px" };
});

function confirm(): void {
  clearFilters();
  track("Advance Source filters applied");
  emit("selectedSources", selectedSources.value);
  emit("close");
}

function clear(): void {
  if (view.value === "results") {
    sourcesListQuery.value = "";
    clearFilters();

    return;
  }

  selectedSourcesQuery.value = "";
  selectedSources.value = [];
}

function closeModal(): void {
  clearFilters();
  emit("close");
}

function editStream(): void {
  if (props.stream?.id) {
    safeRouterPush({
      name: "mention-stream-edit",
      params: {
        streamSlug: props.stream!.slug,
        groupSlug: props.stream!.group?.slug,
      },
    });
  }

  closeModal();
}

watch(totalFilters, () => {
  if (totalFilters) view.value = "results";
});

watch(selectedSources, () => {
  if (!selectedSources.value.length && !isMobile) {
    view.value = "results";
  }
});
</script>
